import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ViewIconWhite from '../../assets/svgs/ViewIconWhite';
import AnnotationIcon from '../../assets/svgs/AnnotationIcon';
import DocumentIcon  from '../../assets/svgs/DocumentIcon';
import ActionButton from '../../components/ActionButton/ActionButton';
import Header from '../../components/Header/Header';
import Loading from '../../components/Loading/Loading';
import Modal from '../../components/Modal/Modal';
import ReportDetails from '../../components/ReportDetails/ReportDetails';
import SingleReportHeader from '../../components/SingleReportHeader/SingleReportHeader';
import SnackBar from '../../components/SnackBar/SnackBar';
import Textarea from '../../components/Textarea/Textarea';
import XRayChart from '../../components/XRayChart/XRayChart';
import WebVisualizer from '../WebVisualizer/WebVisualizer';
import WebVisualizerOHIF from '../WebVisualizerOHIF/WebVisualizerOHIF';
import WebVisualizerStone from '../WebVisualizerStone/WebVisualizerStone';
import WebVisualizerCNN from '../WebVisualizerCNN/WebVisualizerCNN';
import NiftiAnnotationTool from '../NiftiAnnotationTool/NiftiAnnotationTool';

import { useDisableBodyScroll } from '../../utils/useDisableBodyScroll';
import EXAM_MODULES from '../../data/exam-modules';

import { functions, colors } from '../../ui/variables';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import { useIsMounted } from '../../utils/useIsMounted';

import { SingleReportStyle } from './SingleReportStyle';
import RefreshIcon from '../../assets/svgs/RefreshIcon';

const SingleRadiographyReport = () => {
  const history = useHistory();
  const institution = useSelector((state) => state.settings.institution);
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);
  const { id } = useParams();

  const [patientReports, setPatientReports] = useState([]);
  const [justifications, setJustifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meUser, setMeUser] = useState();
  const [report, setReport] = useState();
  const [newReport, setNewReport] = useState();
  const [reportFeedback, setReportFeedback] = useState();
  const [reportFeedbacks, setReportFeedbacks] = useState([]);
  const [reportFeedbackComments, setReportFeedbackComments] = useState('');
  const [approvedReportComments, setApprovedReportComments ] = useState('');
  const [tempFeedbackComments, setTempFeedbackComments] = useState ('');
  const [selectedJustifications, setSelectedJustifications] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [snackBarContent, setSnackBarContent] = useState();
  const [approvedReport, setApprovedReport] = useState(false);
  const [completeSeriesModal, setCompleteSeriesModal] = useState(false);
  const [OHIFModal, setOHIFModal] = useState(false);
  const [stoneModal, setStoneModal] = useState(false);
  const [annotationModal, setAnnotationModal] = useState(false);
  const [xRayClassifiedImage, setXRayClassifiedImage] = useState('');
  const [xRayOriginalImage, setXRayOriginalImage] = useState('');
  const [CNNReportModal, setCNNReportModal] = useState(false);

  const refreshReportFeedbacks = async (reportId, userId) => {
    if (!reportId) {
      return;
    }

    const feedbackResponse = await Api.getReportFeedback();
    const reportReviews = (feedbackResponse || [])
      .filter((item) => item.report === reportId)
      .sort((a, b) => (a.date_created > b.date_created ? 1 : -1));

    const myFeedback = userId
      ? reportReviews.find((item) => item.user_id === userId)
      : null;

    setReportFeedbacks(reportReviews);
    setReportFeedback(myFeedback || null);
    setReportFeedbackComments(myFeedback?.comments || '');
    setTempFeedbackComments(myFeedback?.comments || '');
    setSelectedJustifications(
      myFeedback?.justifications?.map((justification) => justification.id) || []
    );
  };

  useDisableBodyScroll(completeSeriesModal);
  useDisableBodyScroll(OHIFModal);
  useDisableBodyScroll(CNNReportModal);
  useDisableBodyScroll(stoneModal);
  useDisableBodyScroll(annotationModal);

  function corrigeModulos(exam) {
    if (!exam.module) {
      exam.module = EXAM_MODULES.OUTROS.key;
    } else {
      const modulos = Object.keys(EXAM_MODULES);
      const moduloEncontrado = modulos.find(mod => exam.module.includes(mod));
      if (moduloEncontrado) {
        exam.module = EXAM_MODULES[moduloEncontrado].key;
      } else {
        exam.module = EXAM_MODULES.OUTROS.key;
      }
    }
    return exam;
  }

    // Importação dos scripts do anotador
    useEffect(() => {
      const scripts = [
        { id: 'variaveis-script', src: '/js/variaveis.js' },
        { id: 'nifti-reader-script', src: '/js/nifti-reader.js' },
        { id: 'itk-script', src: '/js/itk.js' },
        { id: 'vtk-script', src: '/js/vtk.js' },
        { id: 'mask-utils-script', src: '/js/maskUtils.js' },
        { id: 'mouse-utils-script', src: '/js/mouseUtils.js' },
        { id: 'nifti-utils-script', src: '/js/niftiUtils.js' },
        { id: 'draw-utils-script', src: '/js/drawUtils.js' },
        { id: 'events-script', src: '/js/events.js' },
      ];
    
      scripts.forEach(({ id, src }) => {
        if (!document.getElementById(id)) {
          const script = document.createElement('script');
          script.id = id;
          script.src = src;
          script.async = true;
          document.body.appendChild(script);
        }
      });
    
      return () => {
        scripts.forEach(({ id }) => {
          const script = document.getElementById(id);
          if (script) {
            document.body.removeChild(script);
          }
        });
      };
    }, []);

  useEffect(() => {
    (async () => {
      if (id?.length) {
        if (!isMounted()) {
          return;
        }

        setLoading(true);

        // Carrega primeiro relatorio simplificado
        const reportResponse = await Api.getSimplifiedReport(id);
        let currentReport = null;
        let currentUser = meUser;

        if (!currentUser) {
          currentUser = await Api.getMeUser();
          if (currentUser) {
            setMeUser(currentUser);
          }
        }

        if (reportResponse?.status === 200 && reportResponse?.data) {
          currentReport = reportResponse?.data;
        }

        if (reportResponse?.status === 404 || !currentReport) {
          history.push('/404-exame');
        } else if (reportResponse?.status === 200 && currentReport) {
          setNewReport(currentReport);
          setReport(currentReport);
          setApprovedReport(currentReport.accepted);
          await refreshReportFeedbacks(currentReport.id, currentUser?.id);
          setLoading(false);
        }

        // Carrega assincronamente relatorio completo, exibindo as imagens e video ao finalizar
        (async () => {
          const reportResponse = await Api.getReport(id);
          let currentReport = null;
  
          if (reportResponse?.status === 200 && reportResponse?.data) {
            currentReport = reportResponse?.data;
          }
  
          if (reportResponse?.status === 404 || !currentReport || !['CR', 'DX', 'IO'].includes(currentReport.exam.type.toUpperCase())) {
            history.push('/404-exame');
          } else if (reportResponse?.status === 200 && currentReport) {
            currentReport.exam = corrigeModulos(currentReport.exam)
            setNewReport(currentReport);
            setReport(currentReport);
            setApprovedReport(currentReport.accepted);
            await refreshReportFeedbacks(currentReport.id, currentUser?.id);
          }
        })();
      }
    })();
  }, [history, id, isMounted, meUser]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      // setLoading(true);

      if (report?.exam?.patient?.id) {
        const response = await Api.getReportsMiniByPatient(
          report?.exam?.patient?.id
        );

        if (response?.data?.length) {          
          setPatientReports(response?.data);
        }
      }
    })();
  }, [isMounted, report?.exam?.patient?.id]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      const response = await Api.getMeUser();

      if (response) {
        setMeUser(response);
      }
    })();
  }, [isMounted]);

  useEffect(() => {
    if (!loading) {
      const snackBarTimeout = setTimeout(() => {
        setSnackBarContent(null);
      }, 3000);

      return () => clearTimeout(snackBarTimeout);
    }

    return null;
  }, [loading]);

  useEffect(() => {
    (async () => {
      if (
        report &&
        report.exam &&
        report.exam.type &&
        report.exam.type.length
      ) {
        // if (isMounted()) {
          // setLoading(true);
        // }

        const currentJustifications = await Api.getJustificationsByExamType(
          report.exam.type
        );

        if (isMounted()) {
          if (currentJustifications && currentJustifications.length) {
            setJustifications(currentJustifications);
          }

          setLoading(false);
        }
      }
    })();
  }, [isMounted, report]);

  useEffect(() => {
    if (!loading) {
      const snackBarTimeout = setTimeout(() => {
        setSnackBarContent(null);
      }, 3000);

      return () => clearTimeout(snackBarTimeout);
    }

    return null;
  }, [loading]);

  const canApproveReport = useMemo(() => {
    return (
      institution?.id &&
      meUser &&
      meUser?.permissions_data?.some(
        (element) =>
          element.permission === 'approve_report' &&
          element.institution === institution?.id
      )
    );
  }, [institution, meUser]);

  const handleCloseModal = () => setShowReviewModal(false);

  const handleApprovedReport = (feedback) => {
    (async () => {
      setLoading(true);
      
      if (report && report.id) {
        const response = await Api.approveReport(report.id, feedback);
        if (response && response.status) {
          if (response.status === 200) {
            setApprovedReport(true);
            setSnackBarContent({
              type: 'success',
              text: `${translate('reportSuccessfullyApproved', language)}.`,
            });

            if (response && response.data) {
              setNewReport(response.data);
              setReport(response.data);
            }

            await refreshReportFeedbacks(report.id, meUser?.id);
            setLoading(false);
          } else if (response.status === 403) {
            setSnackBarContent({
              type: 'error',
              text: `${translate(
                'reportNotApprovedBecauseItHasFeedback',
                language
              )}.`,
            });
            setLoading(false);
          } else {
            setSnackBarContent({
              type: 'error',
              text: `${translate('errorApprovingReport', language)}.`,
            });
            setLoading(false);
          }
        }

        setLoading(false);
      }
    })();
  };

  const onClickSubmitRequestReview = async(feedback) => {
    setLoading(true);

    if (report && report.id && justifications && justifications.length) {

        setApprovedReportComments('')
        setTempFeedbackComments(feedback || '')
        if (reportFeedback) {
          await Api.putReportFeedback(reportFeedback.id, {
            id: reportFeedback.id,
            report: report.id,
            justifications: selectedJustifications,
            comments: feedback || '',
          }).then(() => {
            setShowReviewModal(false);
            setLoading(false);
            setSnackBarContent({
              type: 'success',
              text: `${translate('yourReviewWasSentSucessfully', language)}.`,
            });
          })
          //.finally(() => setLoading(false))
          .catch(() => {
            setLoading(false);
            setSnackBarContent({
              type: 'error',
              text: `${translate('errorSubmittingYourReview', language)}.`,
            });
          });

          await refreshReportFeedbacks(report.id, meUser?.id);
      } else {

        await Api.submitReportFeedback({
          report: report.id,
          justifications: selectedJustifications,
          comments: feedback || '',
        })
          .then(() => {
            setShowReviewModal(false);
            setLoading(false)
            setSnackBarContent({
              type: 'success',
              text: `${translate('yourReviewWasSentSucessfully', language)}.`,
            });
          })
          //.finally(() => setLoading(false))
          .catch(() => {
            setLoading(false)
            setSnackBarContent({
              type: 'error',
              text: `${translate('errorSubmittingYourReview', language)}.`,
            });
          });

        const response = await Api.disapproveReport(report.id)
        setReport(response);
        setApprovedReport(false);
        setLoading(false)

        await refreshReportFeedbacks(report.id, meUser?.id);
        
      }
    }
    
    setLoading(false)

  };

  const onClickJustificationItemButton = (justificationId) => {
    const currentSelectedJustifications = selectedJustifications;

    if (currentSelectedJustifications.includes(justificationId)) {
      const justificationIndexOf =
        currentSelectedJustifications.indexOf(justificationId);
      currentSelectedJustifications.splice(justificationIndexOf, 1);
    } else {
      currentSelectedJustifications.push(justificationId);
    }

    setSelectedJustifications([...currentSelectedJustifications]);
  };

    useEffect(() => {
      (async () => {
        if (!isMounted() || !report?.images?.length) {
          return;
        }
  
        setLoading(true);
  
        const promises = [];
        const hasOriginal = !!report?.images[0]?.value?.length;
        const hasClassified = !!report?.exam?.images[0]?.value?.length;
        let originalUrl = '';
        let classifiedUrl = '';
  
        if (hasOriginal) {
          promises.push(Api.getDicomJpegById(report?.images[0]?.value));
        }
  
        if (hasClassified) {
          promises.push(Api.getDicomJpegById(report?.exam?.images[0]?.value));
        }
  
        if (promises?.length) {
          const response = await axios.all(promises);
  
          if (hasOriginal && response[0]) {
            const originalBlob = new Blob([response[0]], { type: 'image/jpeg' });
  
            if (originalBlob) {
              originalUrl = URL.createObjectURL(originalBlob);
            }
          }
  
          if (hasClassified) {
            let classifiedBlob = null;
  
            if (hasOriginal && response[1]) {
              classifiedBlob = new Blob([response[1]], { type: 'image/jpeg' });
            } else if (!hasOriginal && response[0]) {
              classifiedBlob = new Blob([response[0]], { type: 'image/jpeg' });
            }
  
            if (classifiedBlob) {
              classifiedUrl = URL.createObjectURL(classifiedBlob);
            }
          }
        }
  
        if (originalUrl?.length) {
          setXRayOriginalImage(originalUrl);
        }
  
        if (classifiedUrl?.length) {
          setXRayClassifiedImage(classifiedUrl);
        }
  
        setLoading(false);
      })();
    }, [isMounted, report?.exam?.images, report?.images]);

  const hasReportProbability = useMemo(() => {
    if (
      report &&
      report.probability !== null &&
      report.probability !== undefined
    ) {
      return true;
    }

    return false;
  }, [report]);

  return (
    <SingleReportStyle
      className={!patientReports?.length ? 'without-header' : ''}
    >
      <Header />
      {report && patientReports?.length ? (
        <SingleReportHeader
          examType={'radiografias'}
          allReportsMini={patientReports}
          currentReport={report}
          reportFeedbackComments={approvedReportComments}
          // justifications={justifications}
          setLoading={setLoading}
          approvedReport={approvedReport}
          handleRequestReviewModal={() => setShowReviewModal(true)}
          handleApprovedReport={handleApprovedReport}
          setReportFeedbackComments={setApprovedReportComments}
          canApproveReport={canApproveReport}
        />
      ) : null}
      {snackBarContent &&
      snackBarContent.type &&
      snackBarContent.type.length &&
      snackBarContent.text &&
      snackBarContent.text.length ? (
        <div className="snack-bar-wrapper">
          <SnackBar type={snackBarContent.type} text={snackBarContent.text} />
        </div>
      ) : null}
      {report ? (
        <div className="report-content-grid">
          <div className="side-bar">
            <div className="report-details-section">
              <ReportDetails
                report={report}
                feedbacks={reportFeedbacks}
                currentUserId={meUser?.id}
                isAdmin={meUser?.type === 'ADM'}
                onFeedbacksRefresh={() => refreshReportFeedbacks(report?.id, meUser?.id)}
              />
            </div>
            {report.glyph || hasReportProbability ? (
              <div className="glyph-chart-section">
                <h3 className="glyph-chart-section-title">
                  {translate('result', language)}
                </h3>
                { hasReportProbability ? (
                  <XRayChart
                    percent={report.probability}
                    classification={report.classification || ''}
                  />
                ) : null}
              </div>
            ) : null}
            {/* Botões que mostram as séries, o estudo e faz donwload do vídeo da série */}
            
            <div className="container">
              <ActionButton
                onClick={() =>
                  setOHIFModal(true)
                }
                text={translate('viewOHIF', language)}
                icon={<ViewIconWhite />}
                textAlign="left"
              />

              {OHIFModal ? (
                <Modal
                  overlay={`${functions.rgba(colors.black, 0.7)}`}
                  theme="dark"
                  fullScreen
                  handleClose={() => setOHIFModal(false)}
                >
                  <WebVisualizerOHIF id={id} modal={true} />
                </Modal>
              ) : null}

              <div className="space" />

              <ActionButton
                onClick={() =>
                  setCompleteSeriesModal(true)
                }
                text={translate('viewCompleteSeries', language)}
                icon={<ViewIconWhite />}
                textAlign="left"
              />

              {completeSeriesModal ? (
                <Modal
                  overlay={`${functions.rgba(colors.black, 0.7)}`}
                  theme="dark"
                  fullScreen
                  handleClose={() => setCompleteSeriesModal(false)}
                >
                  <WebVisualizer id={id} modal={true} />
                </Modal>
              ) : null}

              <div className="space" />

              <ActionButton
                onClick={() =>
                  setStoneModal(true)
                }
                text={translate('viewStone', language)}
                icon={<ViewIconWhite />}
                textAlign="left"
              />

              {stoneModal ? (
                <Modal
                  overlay={`${functions.rgba(colors.black, 0.7)}`}
                  theme="dark"
                  fullScreen
                  handleClose={() => setStoneModal(false)}
                >
                  <WebVisualizerStone id={id} modal={true} />
                </Modal>
              ) : null}

              <div className="space" />

              <ActionButton
                onClick={() =>
                  setCNNReportModal(true)
                }
                text={translate('cnn', language)}
                icon={<DocumentIcon />}
                textAlign="left"
              />
              {CNNReportModal ? (
                <Modal
                  overlay={`${functions.rgba(colors.black, 0.7)}`}
                  theme="dark"
                  fullScreen
                  handleClose={() => setCNNReportModal(false)}
                >
                  <WebVisualizerCNN id={id} modal={true} report={report} pdf={report.pdf_cnn} />
                </Modal>
              ) : null}
              <div className="space" />
              <ActionButton
                  onClick={() =>
                    setAnnotationModal(true)
                  }
                  text={translate('annotation', language)}
                  icon={<AnnotationIcon />}
                  textAlign="left"
                />
                {annotationModal ? (
                  <Modal
                    overlay={`${functions.rgba(colors.black, 0.7)}`}
                    theme="dark"
                    fullScreen
                    handleClose={() => setAnnotationModal(false)}
                  >
                    <NiftiAnnotationTool isRadiography={true} id={id} modal={true} />
                  </Modal>
                ) : null}
                <div className="space" />
               {meUser?.is_headquarters_admin && (
                 <ActionButton
                   onClick={() =>
                    Api.updateResults(report.exam.id).then(() => {
                      setSnackBarContent({
                        type: 'success',
                        text: `${translate('examUpdateRequestedSuccessfully', language)}.`,
                      });
                      setTimeout(() => setSnackBarContent(null), 3000);
                    }).catch(() => {
                      setSnackBarContent({
                        type: 'error',
                        text: `${translate('errorRequestingExamUpdate', language)}.`,
                      });
                      setTimeout(() => setSnackBarContent(null), 3000);
                    })
                   }
                   text={translate('updateResults', language)}
                   icon={<RefreshIcon />}
                   textAlign="left"
                 />
               )}
            </div>
          </div>
          <div
            className={`report-main-content radiography-content`}
          >
            {(xRayOriginalImage?.length || xRayClassifiedImage?.length) ? (
              <div className="x-ray-image-gallery">
                {xRayOriginalImage?.length ? (
                  <div className="x-ray-image-item">
                    <img
                      className="x-ray-image-item-file"
                      src={xRayOriginalImage}
                      alt="x-ray original"
                    />
                  </div>
                ) : null}
                {xRayClassifiedImage?.length ? (
                  <div className="x-ray-image-item">
                    <img
                      className="x-ray-image-item-file"
                      src={xRayClassifiedImage}
                      alt="x-ray original"
                    />
                  </div>
                ) : null}
              </div>
            )  : null}
            {/* ) : (
              <div className="x-ray-image-gallery">
                <LoadingSection message={translate('loadingExamImages', language)} />
              </div>
            )} */}
          </div>
        </div>
      ) : null}
      {showReviewModal ? (
        <Modal overlay="transparent" withShadow handleClose={handleCloseModal}>
          <div className="review-modal-content">
            <h4 className="review-modal-title">
              {translate('requestReview', language)}
            </h4>
            <p className="review-modal-description">
              {translate('selectOneOrMoreOptionsToGetBetterResults', language)}:
            </p>
            <div className="review-modal-justifications">
              {justifications.map((justification) => (
                <div
                  key={justification.id}
                  className="modal-justification-item"
                >
                  {justification &&
                  justification.name &&
                  justification.name.length ? (
                    <ActionButton
                      theme={
                        selectedJustifications.includes(justification.id)
                          ? 'very-dark'
                          : 'light-disabled'
                      }
                      text={translate(
                        justification.name.toLowerCase(),
                        language
                      )}
                      textAlign="left"
                      transition
                      onClick={() =>
                        onClickJustificationItemButton(justification.id)
                      }
                    />
                  ) : null}
                  {justification &&
                  justification.description &&
                  justification.description.length ? (
                    <p className="type-item-instruction">
                      {translate(
                        justification.description.toLowerCase(),
                        language
                      )}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="review-modal-about-desagree">
              <p className="modal-about-desagree-instruction">
                {translate('tellMoreAboutDisagreeInResults', language)}.
              </p>
              <Textarea
                value={tempFeedbackComments}
                handleChange={(event) =>
                  setTempFeedbackComments(event.currentTarget.value)
                }
                placeholder={translate('typeYourFeedback', language)}
              />
            </div>
            <div className="submit-request-review">
              <ActionButton
                theme={!selectedJustifications.length ? 'disabled' : 'default'}
                text={translate('sendFeedback', language).toUpperCase()}
                disabled={!selectedJustifications.length}
                transition
                onClick={() => {
                  setReportFeedbackComments(tempFeedbackComments);
                  onClickSubmitRequestReview(tempFeedbackComments);
                }}
              />
            </div>
          </div>
        </Modal>
      ) : null}
      {loading ? <Loading /> : null}
    </SingleReportStyle>
  );
};

export default SingleRadiographyReport;
